'use client';

import { useFormikContext } from 'formik';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import Text from '@commons/components/Text';

// Type definition for the custom text on submission.

type Props = {
  showSuccessMessage?: boolean;
  successMessage?: string;
  errorMessage?: string;
  resetFields?: boolean;
};

/**
 * Form status component.
 */

const FormStatus = ({
  successMessage,
  errorMessage,
  resetFields = false,
  showSuccessMessage = false,
}: Props) => {
  const t = useTranslations('forms.status');
  const { status, resetForm, initialValues } = useFormikContext();

  // Reset form fields to initial values and keep status intact.
  useEffect(() => {
    if (status && status.state === 'success' && resetFields) {
      resetForm({ values: initialValues, status });
    }
  }, [status, resetForm, initialValues, resetFields]);

  if (typeof status !== 'object') return null;

  const getStatusMessage = () => {
    switch (status.state) {
      case 'success':
        if (!showSuccessMessage) return null;
        return successMessage || t('success');
      case 'error':
        return errorMessage || t('error');
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  if (statusMessage) {
    return (
      <Text
        color={status.state != 'success' ? 'red-1' : undefined}
        marginBottom="sm"
        marginTop="sm"
      >
        {statusMessage}
      </Text>
    );
  }

  return null;
};

export default FormStatus;
